// @ts-nocheck
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import camelCase from "lodash/camelCase";
import React, { useEffect } from "react";

interface DataAttributeModel {
    fields: {
        attributeName: string;
        attributeValue: string;
    };
}

const enum fields {
    scriptType = "scriptType",
    scriptUrl = "scriptUrl",
    scriptID = "scriptID",
    scriptIntegrity = "scriptIntegrity",
    scriptCrossOrigin = "scriptCrossOrigin",
    isAsync = "isAsync",
    isDefer = "isDefer",
    dataAttributes = "dataAttributes",
}

interface Props extends WidgetProps {
    fields: {
        [fields.scriptType]: "application/javascript" | "text/css";
        [fields.scriptUrl]: string;
        [fields.scriptID]: string;
        [fields.scriptIntegrity]: string;
        [fields.scriptCrossOrigin]: "anonymous" | "use-credentials";
        [fields.isAsync]: boolean;
        [fields.isDefer]: boolean;
        [fields.dataAttributes]: DataAttributeModel[];
    };
}

const HeaderScripts: React.FC<Props> = ({
    fields: { scriptType, scriptUrl, scriptID, isAsync, isDefer, scriptIntegrity, scriptCrossOrigin, dataAttributes },
}) => {
    useEffect(() => {
        const dataAttribute = dataAttributes?.map(item => {
            return {
                name: camelCase(item.fields.attributeName.split(/-(.+)/)[1]),
                value: item.fields.attributeValue,
            };
        });

        const head = document.head;
        let headerScript: HTMLLinkElement | HTMLScriptElement;

        if (scriptType === "text/css") {
            headerScript = document.createElement("link");
            headerScript.rel = "stylesheet";
            headerScript.href = scriptUrl;
        } else {
            headerScript = document.createElement("script");
            headerScript.src = scriptUrl;
            headerScript.async = isAsync;
            headerScript.defer = isDefer;
        }
        headerScript.type = scriptType;
        if (scriptID) {
            headerScript.id = scriptID;
        }
        if (scriptIntegrity) {
            headerScript.integrity = scriptIntegrity;
        }
        if (scriptCrossOrigin) {
            headerScript.crossOrigin = scriptCrossOrigin;
        }
        dataAttribute?.forEach(attributeSet => {
            headerScript.dataset[attributeSet.name] = attributeSet.value;
        });

        head.appendChild(headerScript);

        return () => {
            head.removeChild(headerScript);
        };
    }, []);
    return <div />;
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "code",
    fieldDefinitions: [
        {
            name: fields.scriptType,
            editorTemplate: "DropDownField",
            defaultValue: "application/javascript",
            fieldType: "General",
            options: [
                { displayName: "Javascript", value: "application/javascript" },
                { displayName: "CSS Style", value: "text/css" },
            ],
            hideEmptyOption: true,
        },
        {
            name: fields.scriptUrl,
            displayName: "Script Url",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Script url",
            isRequired: true,
        },
        {
            name: fields.scriptID,
            displayName: "Script ID",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Script ID",
        },
        {
            name: fields.scriptIntegrity,
            displayName: "Integrity Hash",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.scriptCrossOrigin,
            editorTemplate: "DropDownField",
            defaultValue: "",
            fieldType: "General",
            options: [
                { displayName: "Anonymous", value: "anonymous" },
                { displayName: "Use Credentials", value: "use-credentials" },
            ],
        },
        {
            name: fields.isAsync,
            editorTemplate: "CheckboxField",
            fieldType: "General",
            defaultValue: false,
            isVisible: widget => widget.fields.scriptType === "application/javascript",
        },
        {
            name: fields.isDefer,
            editorTemplate: "CheckboxField",
            fieldType: "General",
            defaultValue: false,
            isVisible: widget => widget.fields.scriptType === "application/javascript",
        },
        {
            name: fields.dataAttributes,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.attributeName;
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: "attributeName",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "attributeValue",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: HeaderScripts,
    definition,
};

export default widgetModule;
